import { Checkbox, Grid, makeStyles, Typography } from '@material-ui/core';
import clsx from 'clsx';
import Loading from 'components/Loading';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import i18n from 'i18n';
import PermissionCard from 'containers/Groups/PermissionCard';
import Alert from '@material-ui/lab/Alert';
import styles from '../../Template/styles';
import { ENTITY_NAME } from './Content';

const useStyles = makeStyles(styles);

interface Props {
  title: string;
  subtitle: string;
  isLoading: boolean;
  permissions?: {
    loading: boolean;
    selected: string[];
    available: { name: string; selected: boolean }[];
  };
  pending: string[];
  setPending: (pending: string[]) => void;
  updatePermissions: (permissions: string[]) => void;
}

const ListPermissions = ({
  title,
  subtitle,
  permissions,
  isLoading,
  pending,
  setPending,
  updatePermissions,
}: Props) => {
  const classes = useStyles();
  const [checked, setChecked] = React.useState<string[]>([]);

  const permissionsNames: string[] = permissions?.available?.map(
    item => item.name
  ) || [''];

  const permissionList = [
    {
      key: 'partner',
      label: i18n.t('PARTNER_KEY'),
      values: ['create_new_partner'].filter(item =>
        permissionsNames?.includes(item)
      ),
    },
    {
      key: 'company',
      label: i18n.t('COMPANY_KEY'),
      values: [
        'list_all_companies',
        'list_company_dashboard',
        'list_company_details',
        'list_company_users',
        'edit_company_details',
        'delete_company',
        'can_edit_company_alias',
      ].filter(item => permissionsNames.includes(item)),
    },
  ].filter(item => item.values.length > 0);

  const handleCheck = {
    exists: (value: string) => checked.includes(value),
    toggle: (value: string) => {
      setPending(
        pending.includes(value)
          ? pending.filter(item => item !== value)
          : pending.concat(value)
      );

      const newCheckeds = checked.includes(value)
        ? checked.filter(item => item !== value)
        : [...checked, value];

      setChecked(newCheckeds);
      updatePermissions(newCheckeds);
    },
  };

  React.useEffect(() => {
    if (permissions?.selected) {
      setChecked(permissions.selected);
    }
  }, [permissions]);

  const allPermissionsAvailable = React.useMemo(() => permissionsNames || [], [
    permissionsNames,
  ]);

  const handleCheckAllExists = (values: string[]) =>
    values.reduce(
      (permissionsResponse, currentValue) =>
        permissionsResponse && checked.includes(currentValue),
      true
    );

  const handleToggleAll = (values: string[]) => {
    let newListPending = [...pending];
    values.forEach(value => {
      if (newListPending.includes(value)) {
        newListPending = newListPending.filter(item => item !== value);
      } else {
        newListPending = [...newListPending, value];
      }
    });
    setPending(newListPending);
    if (handleCheckAllExists(values)) {
      setChecked([]);
      updatePermissions([]);
    } else {
      setChecked(values);
      updatePermissions(values);
    }
  };

  return (
    <React.Fragment>
      {permissionList.length === 0 ? (
        <Grid container justify="center" className={classes.warning}>
          <Alert className={classes.alert} severity="warning" color="info">
            <Grid item>
              <Typography>{i18n.t('NO_PERMISSIONS_IN_GROUP_KEY')}</Typography>
            </Grid>
          </Alert>
        </Grid>
      ) : (
        <Grid
          container
          spacing={2}
          className={clsx({
            [classes.disableArea]: permissions?.loading,
          })}
        >
          <Grid item xs={12} style={{ margin: '32px 0 8px' }}>
            <Typography variant="h6">{title}</Typography>
            <Typography variant="body2">{subtitle}</Typography>
          </Grid>
          {(permissions?.loading && !permissionsNames) || isLoading ? (
            <Loading />
          ) : (
            <>
              <Grid container className={classes.superTitle}>
                <Checkbox
                  size="small"
                  checked={handleCheckAllExists(allPermissionsAvailable)}
                  onChange={() => {
                    handleToggleAll(allPermissionsAvailable);
                  }}
                  className={classes.checkIcon}
                />
                <Typography className={classes.titleText}>
                  {i18n.t('GROUPS_PAGE.SELECT_ALL_KEY')}
                </Typography>
              </Grid>
              <PermissionCard
                groupPermissions={permissionList}
                entityKey="company"
                permissions={permissions}
                pending={pending}
                handleCheck={handleCheck}
                handleCheckAllExists={handleCheckAllExists}
                setPending={setPending}
                updatePermissions={updatePermissions}
                checked={checked}
                setChecked={setChecked}
              />
            </>
          )}
        </Grid>
      )}
    </React.Fragment>
  );
};

const mapStateToProps = ({
  permission: { permissions, isLoading },
}: ApplicationState) => ({
  permissions: permissions[ENTITY_NAME],
  isLoading,
});

export default connect(mapStateToProps)(ListPermissions);
